import React, { useState, useEffect, useRef } from "react";
import { Container, Nav, Row, Col } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import {
  getCompanies,
  getLocations,
  getCountriesWithStates,
  getFieldStaff,
  addLocation,
  updateLocation,
} from "./SettingsApi";
import "./Settings.css";

const tabConfig = [{ label: "Settings", key: "settings" }];

const rowsPerPageOptions = [10, 20, 30];

const Settings = () => {
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [fieldStaff, setFieldStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const dragOriginRef = useRef({ x: 0, y: 0 });
  const [locationForm, setLocationForm] = useState({
    locationId: 0,
    name: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zip: "",
    phone: "",
    companyId: "",
    companyName: "",
    fieldStaffId: "",
    isActive: true,
    status: "Active",
    createdBy: 0,
    createdDate: new Date().toISOString(),
    modifiedBy: 0,
    modifiedDate: new Date().toISOString(),
  });
  const [editMode, setEditMode] = useState(false);
  const [paginationData, setPaginationData] = useState({});
  console.log(paginationData);
  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (!showLocationModal) {
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
    }
  }, [showLocationModal]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!isDragging) {
        return;
      }

      setDragOffset({
        x: dragOriginRef.current.x + (event.clientX - dragStartRef.current.x),
        y: dragOriginRef.current.y + (event.clientY - dragStartRef.current.y),
      });
    };

    const handlePointerUp = () => {
      if (!isDragging) {
        return;
      }

      setIsDragging(false);
      dragOriginRef.current = dragOffset;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, dragOffset]);

  // Helper to read companyname/id from various possible API shapes
  const getCompanyName = (c) => {
    if (!c) return "";
    return (
      c.name || c.companyName || c.company || c.displayName || c.company_display_name || ""
    );
  };

  const getCompanyId = (c) => {
    if (!c) return "";
    return c.companyId || c.id || c.companyID || c.CompanyID || "";
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await fetchLocationsData();
      await Promise.allSettled([
        fetchCompaniesData(),
        fetchCountriesWithStatesData(),
        fetchFieldStaffData(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationsData = async (
    search,
    currentPage = 1,
    rowsPerPage = 25
  ) => {
    try {
      const response = await getLocations(currentPage, rowsPerPage, search);
      const { totalRecords, pageNumber, pageSize, totalPages } =
        response.results;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      if (!response.success || !response.results) {
        setData([]);
        return;
      }
      const transformedData = response.results.results.map((location) => ({
        id: location.locationId,
        company: location.companyName || "N/A",
        location: location.name,
        country: location.country,
        state: location.state,
        city: location.city,
        status: location.isActive ? "Active" : "Pending for approval",
        address: location.address,
        zip: location.zip,
        phone: location.phone,
        companyId: location.companyId,
        fieldStaffId: location.fieldStaffId,
        locationId: location.locationId,
      }));
      setData(transformedData);
    } catch (error) {
      setData([]);
    }
  };

  const fetchCompaniesData = async () => {
    try {
      const response = await getCompanies();
      if (response.success && response.results) {
        setCompanies(response.results.results);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchCountriesWithStatesData = async () => {
    try {
      const countriesData = await getCountriesWithStates();
      const countriesArray = Array.isArray(countriesData)
        ? countriesData
        : countriesData.results || [];
      setCountries(countriesArray);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchFieldStaffData = async () => {
    try {
      const response = await getFieldStaff();
      let staffArray = [];
      if (response.success && response.results) {
        staffArray = response.results;
      } else if (Array.isArray(response)) {
        staffArray = response;
      } else if (response.data && Array.isArray(response.data)) {
        staffArray = response.data;
      }
      setFieldStaff(staffArray);
    } catch (error) {
      console.error("Error fetching field staff:", error);
    }
  };

  const handleCountryChange = (selectedCountry) => {
    const countryData = countries.find((c) => c.country === selectedCountry);
    setStates(
      countryData && countryData.country === "United States of America"
        ? countryData.states
        : []
    );
    setLocationForm((prev) => ({
      ...prev,
      country: selectedCountry,
      state: "",
    }));
  };

  const handleEditClick = (row) => {
    const selectedCompany = (companies || []).find(
      (c) => getCompanyId(c) == row.companyId || getCompanyName(c) === row.company
    );
    setLocationForm({
      locationId: row.locationId,
      name: row.location,
      address: row.address || "",
      country: row.country,
      state: row.state,
      city: row.city,
      zip: row.zip || "",
      phone: row.phone || "",
      companyId: row.companyId,
      companyName: selectedCompany ? getCompanyName(selectedCompany) : row.company,
      fieldStaffId: row.fieldStaffId || "",
      isActive: row.status === "Active",
      status: row.status,
      createdBy: 0,
      createdDate: new Date().toISOString(),
      modifiedBy: 0,
      modifiedDate: new Date().toISOString(),
    });
    const countryData = countries.find((c) => c.country === row.country);
    setStates(
      countryData && countryData.country === "United States of America"
        ? countryData.states
        : []
    );
    setEditMode(true);
    setShowLocationModal(true);
  };

  const handleLocationInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      handleCountryChange(value);
    } else {
      setLocationForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleLocationModalHeaderPointerDown = (event) => {
    if (event.button !== 0) {
      return;
    }

    if (event.target.closest("button, input, select, textarea, a")) {
      return;
    }

    dragStartRef.current = { x: event.clientX, y: event.clientY };
    dragOriginRef.current = dragOffset;
    setIsDragging(true);
  };

  const handleAddLocationClick = () => {
    // Prefer using the company from the first visible table row so Add Location
    const defaultRowCompany = data && data.length > 0 ? { companyId: data[0].companyId, companyName: data[0].company } : null;
    const defaultCompany = defaultRowCompany
      ? defaultRowCompany
      : (companies || []).length > 0
      ? { companyId: getCompanyId(companies[0]), companyName: getCompanyName(companies[0]) }
      : null;

    setLocationForm({
      locationId: 0,
      name: "",
      address: "",
      country: "",
      state: "",
      city: "",
      zip: "",
      phone: "",
      companyId: defaultCompany ? defaultCompany.companyId : "",
      companyName: defaultCompany ? defaultCompany.companyName : "",
      fieldStaffId: "",
      isActive: true,
      status: "Active",
      createdBy: 0,
      createdDate: new Date().toISOString(),
      modifiedBy: 0,
      modifiedDate: new Date().toISOString(),
    });

    setStates([]);
    setEditMode(false);
    setShowLocationModal(true);
  };

  const handleAddLocation = async () => {
    const isUS = locationForm.country === "United States of America";

    if (
      !locationForm.companyId ||
      !locationForm.name ||
      !locationForm.address ||
      !locationForm.country ||
      !locationForm.state ||
      !locationForm.city ||
      (isUS && !locationForm.zip) ||
      !locationForm.fieldStaffId
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      var userRole = JSON.parse(localStorage.getItem("currentRole"));
      const locationData = {
        locationId: 0,
        name: locationForm.name,
        address: locationForm.address,
        country: locationForm.country,
        state: locationForm.state,
        city: locationForm.city,
        zip: locationForm.zip,
        phone: locationForm.phone || "",
        companyId: parseInt(locationForm.companyId),
        companyName: locationForm.companyName,
        fieldStaffId: locationForm.fieldStaffId
          ? parseInt(locationForm.fieldStaffId)
          : null,
        isActive: userRole === "Customer" ? false : locationForm.isActive,
        status:
          userRole === "Customer"
            ? "Pending for approval"
            : locationForm.isActive
            ? "Active"
            : "Pending for approval",
        createdBy: 0,
        createdDate: new Date().toISOString(),
        modifiedBy: 0,
        modifiedDate: new Date().toISOString(),
      };

      await addLocation(locationData);
      toast.success("Location added successfully");
      setShowLocationModal(false);
      resetForm();
      await fetchLocationsData();
    } catch (error) {
      toast.error("Failed to add location");
    }
  };

  const handleUpdateLocation = async () => {
    const isUS = locationForm.country === "United States of America";

    if (
      !locationForm.companyId ||
      !locationForm.name ||
      !locationForm.address ||
      !locationForm.country ||
      !locationForm.state ||
      !locationForm.city ||
      (isUS && !locationForm.zip) ||
      !locationForm.fieldStaffId
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      var userRole = JSON.parse(localStorage.getItem("currentRole"));
      const locationData = {
        locationId: locationForm.locationId,
        name: locationForm.name,
        address: locationForm.address,
        country: locationForm.country,
        state: locationForm.state,
        city: locationForm.city,
        zip: locationForm.zip,
        phone: locationForm.phone || "",
        companyId: parseInt(locationForm.companyId),
        companyName: locationForm.companyName,
        fieldStaffId: locationForm.fieldStaffId
          ? parseInt(locationForm.fieldStaffId)
          : null,
        isActive: userRole === "Customer" ? false : locationForm.isActive,
        status: locationForm.isActive ? "Active" : "Pending for approval",
        createdBy: 0,
        createdDate: locationForm.createdDate,
        modifiedBy: 0,
        modifiedDate: new Date().toISOString(),
      };

      await updateLocation(locationData);
      toast.success("Location updated successfully");
      setShowLocationModal(false);
      resetForm();
      await fetchLocationsData();
    } catch (error) {
      toast.error("Failed to update location");
    }
  };

  const resetForm = () => {
    setLocationForm({
      locationId: 0,
      name: "",
      address: "",
      country: "",
      state: "",
      city: "",
      zip: "",
      phone: "",
      companyId: "",
      companyName: "",
      fieldStaffId: "",
      isActive: true,
      status: "Active",
      createdBy: 0,
      createdDate: new Date().toISOString(),
      modifiedBy: 0,
      modifiedDate: new Date().toISOString(),
    });
    setEditMode(false);
    setStates([]);
  };

  const headerColumns = [
    { header: "Company", accessor: "company", disableSort: true },
    { header: "Location", accessor: "location", disableSort: true },
    { header: "Country", accessor: "country", disableSort: true },
    { header: "State", accessor: "state", disableSort: true },
    { header: "City", accessor: "city", disableSort: true },
    { header: "Status", accessor: "status", disableSort: true },
    {
      header: "Action",
      accessor: "action",
      disableSort: true,
      cell: (row) => (
        <Button
          variant="outline-primary"
          size="sm"
          className="settings-table-edit-btn"
          onClick={() => handleEditClick(row)}
          title="Edit location"
        >
          Edit
        </Button>
      ),
    },
  ];

  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const renderRow = (row) => (
    <tr key={row.id}>
      <td>{row.company}</td>
      <td>{row.location}</td>
      <td>{row.country}</td>
      <td>{row.state}</td>
      <td>{row.city}</td>
      <td>
        <span>{row.status}</span>
      </td>
      <td>
        <Button
          variant="edit-active"
          size="sm"
          onClick={() => handleEditClick(row)}
          title="Edit location"
        >
          Edit
        </Button>
      </td>
    </tr>
  );

  return (
    <div>
      <ToastContainer position="top-right" autoClose={6000} />
      <Container fluid className="settings-second-tab-container">
        <Nav
          variant="underline"
          activeKey="settings"
          className="flex-nowrap nav-underline"
        >
          {tabConfig.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                className="default-font-styles"
                active
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Container>

      <div className="settings-tab-content-container p-3">
        <div className="settings-details-content">
          <Row className="align-items-center mb-4 settings-search-filter-bar">
            <Col />
            <Col xs="auto" className="d-flex align-items-center gap-2">
              <Button
                variant="outlined"
                size="md"
                className="settings-add-location-btn"
                onClick={handleAddLocationClick}
                title="Add a new location"
              >
                Add Location
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="settings-table-section mb-4">
        <SimpleTable
          columns={headerColumns}
          data={data}
          customRowRender={renderRow}
          showPagination
          paginationData={paginationData}
          searchValue={""}
          fetchData={fetchLocationsData}
        />
      </div>

      {showLocationModal && (
        <div className="settings-management-container">
          <div
            className={`settings-modal-content${isDragging ? " settings-modal-dragging" : ""}`}
            style={{
              "--settings-modal-drag-x": `${dragOffset.x}px`,
              "--settings-modal-drag-y": `${dragOffset.y}px`,
            }}
          >
            <div
              className="settings-modal-header"
              onPointerDown={handleLocationModalHeaderPointerDown}
            >
              <h5 className="settings-modal-title">
                {editMode ? "Edit Location" : "Add Location"}
              </h5>
              <button
                type="button"
                className="settings-modal-close-button"
                title="Close modal"
                onClick={() => {
                  setShowLocationModal(false);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>

            <div className="settings-modal-body">
              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={locationForm.companyName}
                    disabled
                    className="settings-disabled-input"
                    title="Company name"
                  />
                </div>
                <div className="settings-form-group">
                  <label>
                    Location Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={locationForm.name}
                    onChange={handleLocationInputChange}
                    placeholder="Enter Location Name"
                    required
                    className="settings-form-input"
                    title="Enter location name"
                  />
                </div>
                <div className="settings-form-group">
                  <label>
                    Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={locationForm.address}
                    onChange={handleLocationInputChange}
                    placeholder="Enter Address"
                    required
                    className="settings-form-input"
                    title="Enter address"
                  />
                </div>
              </div>

              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>
                    Country <span className="text-danger">*</span>
                  </label>
                  <select
                    name="country"
                    value={locationForm.country}
                    onChange={handleLocationInputChange}
                    required
                    className="settings-form-select"
                    title="Select country"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.country} value={country.country}>
                        {country.country}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="settings-form-group">
                  <label>
                    State <span className="text-danger">*</span>
                  </label>
                  {locationForm.country === "United States of America" ? (
                    <select
                      name="state"
                      value={locationForm.state}
                      onChange={handleLocationInputChange}
                      required
                      disabled={!locationForm.country}
                      className={`settings-form-select ${
                        !locationForm.country ? "settings-disabled-select" : ""
                      }`}
                      title="Select state"
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="state"
                      value={locationForm.state}
                      onChange={handleLocationInputChange}
                      placeholder="Enter State"
                      required
                      className="settings-form-input"
                      title="Enter state"
                    />
                  )}
                </div>

                <div className="settings-form-group">
                  <label>
                    City <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={locationForm.city}
                    onChange={handleLocationInputChange}
                    placeholder="Enter City"
                    required
                    className="settings-form-input"
                    title="Enter city"
                  />
                </div>
              </div>

              <div className="settings-form-row">
                <div className="settings-form-group">
                  <label>
                    Zip{" "}
                    {locationForm.country === "United States of America" && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="zip"
                    value={locationForm.zip}
                    onChange={handleLocationInputChange}
                    placeholder="Enter Zip"
                    {...(locationForm.country === "United States of America"
                      ? { required: true }
                      : {})}
                    className="settings-form-input"
                    title="Enter zip code"
                  />
                </div>
                <div className="settings-form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={locationForm.phone}
                    onChange={handleLocationInputChange}
                    placeholder="Enter Phone"
                    className="settings-form-input"
                    title="Enter phone number"
                  />
                </div>
                <div className="settings-form-group">
                  <label>
                    Phibro Sales Rep <span className="text-danger">*</span>
                  </label>
                  <select
                    name="fieldStaffId"
                    value={locationForm.fieldStaffId}
                    onChange={handleLocationInputChange}
                    className="settings-form-select"
                    required
                    title="Select Phibro sales representative"
                  >
                    <option value="">Select Phibro Rep</option>
                    {fieldStaff.map((staff) => (
                      <option key={staff.userID} value={staff.userID}>
                        {staff.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="settings-modal-footer">
              <Button
                variant="outlined"
                onClick={() => {
                  setShowLocationModal(false);
                  resetForm();
                }}
                title="Cancel and close modal"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={editMode ? handleUpdateLocation : handleAddLocation}
                title={editMode ? "Update location" : "Add new location"}
              >
                {editMode ? "Update" : "Add Location"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
